// Growth evaluation functions based on WHO standards
import { zScoreWeightForAgeBoys, zScoreWeightForAgeGirls } from './zScores';

// Calculate Z-Score using WHO LMS method
export function calculateZScore(value, L, M, S) {
    if (L !== 0) {
        return (Math.pow(value / M, L) - 1) / (L * S);
    } else {
        // For L = 0, use log transformation
        return Math.log(value / M) / S;
    }
}

// --- Helper function for Linear Interpolation ---
function interpolate(x, x0, y0, x1, y1) {
    // Avoid division by zero if data points are identical
    if (x1 - x0 === 0) return y0;
    // Formula for linear interpolation
    return y0 + (x - x0) * (y1 - y0) / (x1 - x0);
}

// Get LMS parameters for weight-for-age
function getLMSParameters(ageInMonths, gender = 'male') {
    const dataSource = gender === 'female' ? zScoreWeightForAgeGirls : zScoreWeightForAgeBoys;
    
    // Handle edge cases: if age is below or above our data range, return the closest boundary.
    if (ageInMonths <= dataSource[0].age) {
        return dataSource[0];
    }
    if (ageInMonths >= dataSource[dataSource.length - 1].age) {
        return dataSource[dataSource.length - 1];
    }

    // Find the two data points surrounding the target age
    let lowerBound, upperBound;
    for (let i = 0; i < dataSource.length - 1; i++) {
        if (ageInMonths >= dataSource[i].age && ageInMonths < dataSource[i + 1].age) {
            lowerBound = dataSource[i];
            upperBound = dataSource[i + 1];
            break;
        }
    }

    // Interpolate L, M, and S values for the exact age
    const L = interpolate(ageInMonths, lowerBound.age, lowerBound.L, upperBound.age, upperBound.L);
    const M = interpolate(ageInMonths, lowerBound.age, lowerBound.M, upperBound.age, upperBound.M);
    const S = interpolate(ageInMonths, lowerBound.age, lowerBound.S, upperBound.age, upperBound.S);

    return { age: ageInMonths, L, M, S };
}

// Evaluate weight-for-age
export function evaluateWeightForAge(weight, ageInMonths, gender = 'male') {
    const lmsParams = getLMSParameters(ageInMonths, gender);
    
    if (!lmsParams) {
        return {
            status: 'Data Tidak Tersedia',
            interpretation: 'Data standar untuk usia dan jenis kelamin ini tidak tersedia.',
            zScore: null
        };
    }

    const zScore = calculateZScore(weight, lmsParams.L, lmsParams.M, lmsParams.S);
    
    let status, interpretation, recommendation;
    
    if (zScore < -3) {
        status = 'Gizi Buruk';
        interpretation = 'Berat badan sangat kurang (severely underweight)';
        recommendation = 'Segera rujuk ke fasilitas kesehatan untuk penanganan gizi buruk. Perlu intervensi medis dan gizi intensif.';
    } else if (zScore < -2) {
        status = 'Gizi Kurang';
        interpretation = 'Berat badan kurang (underweight)';
        recommendation = 'Konsultasi dengan tenaga kesehatan. Perbaiki asupan gizi dan pantau pertumbuhan secara rutin.';
    } else if (zScore <= 1) {
        status = 'Normal';
        interpretation = 'Berat badan normal sesuai usia';
        recommendation = 'Pertahankan pola makan sehat dan seimbang. Lanjutkan pemantauan pertumbuhan rutin.';
    } else if (zScore <= 2) {
        status = 'Risiko Gizi Lebih';
        interpretation = 'Berat badan di atas normal (possible risk of overweight)';
        recommendation = 'Perhatikan pola makan dan aktivitas fisik. Konsultasi dengan ahli gizi jika diperlukan.';
    } else {
        status = 'Gizi Lebih';
        interpretation = 'Berat badan berlebih (overweight)';
        recommendation = 'Konsultasi dengan dokter dan ahli gizi. Atur pola makan dan tingkatkan aktivitas fisik.';
    }

    return {
        status,
        interpretation,
        recommendation,
        zScore: Math.round(zScore * 100) / 100 // Round to 2 decimal places
    };
}

// Simplified height evaluation (using percentile approach)
export function evaluateHeightForAge(height, ageInMonths, gender = 'male') {
    // This is a simplified evaluation - in real implementation, you'd use WHO height-for-age tables
    const expectedHeight = getExpectedHeight(ageInMonths, gender);
    
    if (!expectedHeight) {
        return {
            status: 'Data Tidak Tersedia',
            interpretation: 'Data standar tinggi badan untuk usia ini tidak tersedia.',
            percentile: null
        };
    }

    const deviation = ((height - expectedHeight.median) / expectedHeight.median) * 100;
    
    let status, interpretation, recommendation;
    
    if (deviation < -15) {
        status = 'Sangat Pendek';
        interpretation = 'Tinggi badan sangat kurang dari standar usia (severe stunting)';
        recommendation = 'Segera konsultasi dengan dokter anak. Perlu evaluasi medis dan intervensi gizi.';
    } else if (deviation < -10) {
        status = 'Pendek';
        interpretation = 'Tinggi badan kurang dari standar usia (stunting)';
        recommendation = 'Konsultasi dengan tenaga kesehatan. Perbaiki asupan gizi dan pantau pertumbuhan.';
    } else if (deviation <= 10) {
        status = 'Normal';
        interpretation = 'Tinggi badan sesuai dengan standar usia';
        recommendation = 'Pertahankan asupan gizi yang baik dan lanjutkan pemantauan rutin.';
    } else {
        status = 'Tinggi';
        interpretation = 'Tinggi badan di atas rata-rata untuk usia';
        recommendation = 'Tinggi badan baik. Lanjutkan pola hidup sehat dan pemantauan rutin.';
    }

    return {
        status,
        interpretation,
        recommendation,
        deviation: Math.round(deviation * 10) / 10
    };
}

// Get expected height (simplified - should use WHO data)
function getExpectedHeight(ageInMonths, gender) {
    // Simplified height standards (in cm) - this should be replaced with WHO data
    const heightStandards = {
        male: [
            { age: 0, median: 49.9 },
            { age: 6, median: 67.6 },
            { age: 12, median: 75.7 },
            { age: 18, median: 82.3 },
            { age: 24, median: 87.8 },
            { age: 36, median: 96.1 },
            { age: 48, median: 103.3 },
            { age: 60, median: 109.9 }
        ]
    };

    const standards = heightStandards[gender] || heightStandards.male;
    return standards.reduce((prev, curr) => 
        Math.abs(curr.age - ageInMonths) < Math.abs(prev.age - ageInMonths) ? curr : prev
    );
}

// Evaluate head circumference
export function evaluateHeadCircumference(headCirc, ageInMonths, gender = 'male') {
    const expectedHC = getExpectedHeadCircumference(ageInMonths, gender);
    
    if (!expectedHC) {
        return {
            status: 'Data Tidak Tersedia',
            interpretation: 'Data standar lingkar kepala untuk usia ini tidak tersedia.',
        };
    }

    const deviation = ((headCirc - expectedHC.median) / expectedHC.median) * 100;
    
    let status, interpretation, recommendation;
    
    if (deviation < -10) {
        status = 'Mikrosefali';
        interpretation = 'Lingkar kepala sangat kecil dari standar usia';
        recommendation = 'Segera konsultasi dengan dokter anak untuk evaluasi neurologis lebih lanjut.';
    } else if (deviation < -5) {
        status = 'Kecil';
        interpretation = 'Lingkar kepala di bawah rata-rata';
        recommendation = 'Pantau perkembangan dan konsultasi dengan tenaga kesehatan.';
    } else if (deviation <= 5) {
        status = 'Normal';
        interpretation = 'Lingkar kepala sesuai dengan standar usia';
        recommendation = 'Lingkar kepala normal. Lanjutkan pemantauan rutin.';
    } else if (deviation <= 10) {
        status = 'Besar';
        interpretation = 'Lingkar kepala di atas rata-rata';
        recommendation = 'Pantau perkembangan dan konsultasi jika ada kekhawatiran.';
    } else {
        status = 'Makrosefali';
        interpretation = 'Lingkar kepala sangat besar dari standar usia';
        recommendation = 'Konsultasi dengan dokter anak untuk evaluasi lebih lanjut.';
    }

    return {
        status,
        interpretation,
        recommendation,
        deviation: Math.round(deviation * 10) / 10
    };
}

// Get expected head circumference (simplified)
function getExpectedHeadCircumference(ageInMonths, gender) {
    // Simplified head circumference standards (in cm)
    const hcStandards = {
        male: [
            { age: 0, median: 34.5 },
            { age: 6, median: 43.3 },
            { age: 12, median: 46.1 },
            { age: 18, median: 47.8 },
            { age: 24, median: 48.9 },
            { age: 36, median: 50.0 },
            { age: 48, median: 50.8 },
            { age: 60, median: 51.4 }
        ]
    };

    const standards = hcStandards[gender] || hcStandards.male;
    return standards.reduce((prev, curr) => 
        Math.abs(curr.age - ageInMonths) < Math.abs(prev.age - ageInMonths) ? curr : prev
    );
}

// Comprehensive growth evaluation
export function evaluateGrowth(weight, height, headCirc, ageInMonths, gender = 'male') {
    const weightEval = evaluateWeightForAge(weight, ageInMonths, gender);
    const heightEval = evaluateHeightForAge(height, ageInMonths, gender);
    const hcEval = evaluateHeadCircumference(headCirc, ageInMonths, gender);

    // Determine overall status
    let overallStatus = 'Normal';
    let overallRecommendation = 'Pertumbuhan dalam batas normal. Lanjutkan pemantauan rutin.';

    const criticalConditions = ['Gizi Buruk', 'Sangat Pendek', 'Mikrosefali', 'Makrosefali'];
    const concerningConditions = ['Gizi Kurang', 'Pendek', 'Gizi Lebih', 'Kecil', 'Besar'];

    if (criticalConditions.some(condition => 
        [weightEval.status, heightEval.status, hcEval.status].includes(condition))) {
        overallStatus = 'Perlu Rujukan Segera';
        overallRecommendation = 'Ditemukan kondisi yang memerlukan penanganan medis segera. Rujuk ke fasilitas kesehatan.';
    } else if (concerningConditions.some(condition => 
        [weightEval.status, heightEval.status, hcEval.status].includes(condition))) {
        overallStatus = 'Perlu Perhatian';
        overallRecommendation = 'Ditemukan beberapa aspek pertumbuhan yang perlu dipantau. Konsultasi dengan tenaga kesehatan.';
    }

    return {
        weight: weightEval,
        height: heightEval,
        headCircumference: hcEval,
        overall: {
            status: overallStatus,
            recommendation: overallRecommendation
        }
    };
}
